import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Person } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type {
  CandidateDomainEntry,
  CandidateSkillEntry,
  DomainInput,
  SkillInput,
} from '../../types/candidateDetails';
import {
  SKILL_PROFICIENCIES,
  SKILL_ROLES,
} from '../../types/candidateDetails';
// @ts-ignore
import { peopleApi, skillsApi, type SkillCatalogEntry } from '../../services/api';

/**
 * Phase 3E-3 (Commit 3 — 3A-3) — full-screen editor for an external
 * candidate's skills + domains collections.
 *
 * Two shadow-layered PUTs are fired sequentially on Save:
 *   1. PUT /api/people/:id/skills  — replaces the admin_edit layer only.
 *   2. PUT /api/people/:id/domains — full-wipe (UNIQUE(person, domain)
 *      precludes shadow layering). projection_locks.domains is stamped
 *      so CV re-import leaves manual edits alone.
 *
 * If skills save succeeds but domains fails, the skills write is kept
 * and the modal stays open with a domain-scoped error; the user can
 * retry or close without losing the already-persisted skills.
 *
 * Typeahead is fed by `GET /api/skills/ontology` (any authenticated
 * user). For admins, the dropdown adds a "Create new skill" row that
 * calls `POST /api/skills` (admin-only) and returns a fresh skill_id
 * to attach. The server dedups by normalized_name, so typing "React.js"
 * after "React" already exists returns the existing id instead of
 * minting a duplicate.
 *
 * Wireframe: docs/designs/candidate-edit-flows.svg (section C).
 */

import { adminProfileApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';

/** Years-of-experience inputs on skill and domain rows (wide enough for two-digit values). */
export const YEARS_EXPERIENCE_INPUT_CLASS =
  'w-24 min-w-[5.5rem] shrink-0 px-2 py-1 text-sm tabular-nums text-center rounded bg-dark-card border border-dark-border text-dark-text';

export interface SkillsEditorModalProps {
  person: Person;
  apiClient?: ProfileMutationsApi;
  /** When true, exposes the admin-only "Create new skill" row in the typeahead. */
  isAdmin?: boolean;
  onClose: () => void;
  onSaved: (updated: Person) => void;
}

// Form state ----------------------------------------------------------------

interface SkillForm {
  localId: string;
  skill_id: string;
  skill_name: string;
  skill_role: string;
  proficiency: string;
  years_experience: string; // kept as string for the <input>; parsed on submit
}

interface DomainForm {
  localId: string;
  domain: string;
  years_experience: string;
}

let _localIdCounter = 0;
function nextLocalId(prefix: string): string {
  _localIdCounter += 1;
  return `${prefix}-${_localIdCounter}-${Date.now().toString(36)}`;
}

function skillFromEntry(s: CandidateSkillEntry): SkillForm {
  return {
    localId: nextLocalId('sk'),
    skill_id: s.skillId,
    skill_name: s.skillName ?? s.skillId,
    skill_role: s.role ?? '',
    proficiency: s.proficiency ?? '',
    years_experience: s.yearsExperience != null ? String(s.yearsExperience) : '',
  };
}

function domainFromEntry(d: CandidateDomainEntry): DomainForm {
  return {
    localId: nextLocalId('dm'),
    domain: d.domain,
    years_experience: d.yearsExperience != null ? String(d.yearsExperience) : '',
  };
}

function skillToPayload(s: SkillForm): SkillInput {
  const years = s.years_experience.trim();
  return {
    skill_id: s.skill_id || null,
    skill_role: s.skill_role || null,
    proficiency: s.proficiency || null,
    years_experience: years === '' ? null : Number(years),
    last_used_year: null,
  };
}

function domainToPayload(d: DomainForm): DomainInput {
  const years = d.years_experience.trim();
  return {
    domain: d.domain.trim() || null,
    years_experience: years === '' ? null : Number(years),
  };
}

// Dotted-path error helper --------------------------------------------------

interface ParsedError {
  collection: 'skills' | 'domains' | null;
  index: number | null;
  leaf: string;
}

function parseErrorField(field: string | undefined): ParsedError | null {
  if (!field) return null;
  const m = /^(skills|domains)\[(\d+)\]\.?(.*)$/.exec(field);
  if (!m) return { collection: null, index: null, leaf: field };
  return {
    collection: m[1] as 'skills' | 'domains',
    index: Number(m[2]),
    leaf: m[3] ?? '',
  };
}

// Component -----------------------------------------------------------------

export const SkillsEditorModal: React.FC<SkillsEditorModalProps> = ({
  person,
  apiClient = adminProfileApi,
  isAdmin = false,
  onClose,
  onSaved,
}) => {
  const initialSkills = useMemo<SkillForm[]>(() => {
    const src = person.CandidateDetails?.skills ?? [];
    return src.map(skillFromEntry);
  }, [person]);
  const initialDomains = useMemo<DomainForm[]>(() => {
    const src = person.CandidateDetails?.domains ?? [];
    return src.map(domainFromEntry);
  }, [person]);

  const [skills, setSkills] = useState<SkillForm[]>(initialSkills);
  const [domains, setDomains] = useState<DomainForm[]>(initialDomains);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);

  // Typeahead state
  const [catalog, setCatalog] = useState<SkillCatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const modalTrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalTrapRef, true);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    skillsApi
      .getOntology()
      .then((rows) => {
        if (!cancelled) setCatalog(rows);
      })
      .catch(() => {
        /* silent — typeahead just shows empty list on error */
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const parsedError = useMemo(() => parseErrorField(fieldError?.field), [fieldError]);

  const skillsDirty = useMemo(() => {
    if (skills.length !== initialSkills.length) return true;
    return (
      JSON.stringify(skills.map(skillToPayload)) !==
      JSON.stringify(initialSkills.map(skillToPayload))
    );
  }, [skills, initialSkills]);

  const domainsDirty = useMemo(() => {
    if (domains.length !== initialDomains.length) return true;
    return (
      JSON.stringify(domains.map(domainToPayload)) !==
      JSON.stringify(initialDomains.map(domainToPayload))
    );
  }, [domains, initialDomains]);

  const isDirty = skillsDirty || domainsDirty;

  const confirmClose = useCallback(
    (next: () => void) => {
      if (!isDirty) {
        next();
        return;
      }
      const ok = window.confirm(
        'You have unsaved skill or domain changes. Discard them and close the editor?'
      );
      if (ok) next();
    },
    [isDirty]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') confirmClose(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmClose, onClose]);

  // Typeahead filtering — case-insensitive substring match, capped to 8 rows.
  const q = query.trim().toLowerCase();
  const attachedIds = useMemo(() => new Set(skills.map((s) => s.skill_id)), [skills]);
  const typeaheadMatches = useMemo(() => {
    if (!q) return [] as SkillCatalogEntry[];
    return catalog
      .filter((c) => c.name.toLowerCase().includes(q) && !attachedIds.has(c.id))
      .slice(0, 8);
  }, [catalog, q, attachedIds]);

  const exactMatch = useMemo(() => {
    if (!q) return null;
    return (
      catalog.find((c) => c.name.toLowerCase() === q || c.id.toLowerCase() === q) ?? null
    );
  }, [catalog, q]);

  const attachSkill = useCallback(
    (entry: SkillCatalogEntry) => {
      if (attachedIds.has(entry.id)) return;
      setSkills((prev) => [
        ...prev,
        {
          localId: nextLocalId('sk'),
          skill_id: entry.id,
          skill_name: entry.name,
          skill_role: '',
          proficiency: '',
          years_experience: '',
        },
      ]);
      setQuery('');
      setFieldError(null);
    },
    [attachedIds]
  );

  const handleCreateNewSkill = async () => {
    const rawName = query.trim();
    if (!rawName || creating || !isAdmin) return;
    setCreating(true);
    setServerError(null);
    try {
      const result = await skillsApi.create(rawName);
      const entry: SkillCatalogEntry = { id: result.id, name: result.name };
      // Keep the catalog in sync so subsequent typeahead searches find it.
      setCatalog((prev) => {
        if (prev.some((c) => c.id === entry.id)) return prev;
        return [...prev, entry].sort((a, b) => a.name.localeCompare(b.name));
      });
      attachSkill(entry);
    } catch (e: any) {
      setServerError(e?.response?.data?.error || e?.message || 'Failed to create skill');
    } finally {
      setCreating(false);
    }
  };

  // Skills row handlers
  const patchSkill = (idx: number, mut: (s: SkillForm) => SkillForm) => {
    setSkills((prev) => prev.map((s, i) => (i === idx ? mut(s) : s)));
    setFieldError(null);
  };
  const removeSkill = (idx: number) => {
    setSkills((prev) => prev.filter((_, i) => i !== idx));
    setFieldError(null);
  };

  // Domain row handlers
  const addDomain = () => {
    setDomains((prev) => [
      ...prev,
      { localId: nextLocalId('dm'), domain: '', years_experience: '' },
    ]);
  };
  const patchDomain = (idx: number, mut: (d: DomainForm) => DomainForm) => {
    setDomains((prev) => prev.map((d, i) => (i === idx ? mut(d) : d)));
    setFieldError(null);
  };
  const removeDomain = (idx: number) => {
    setDomains((prev) => prev.filter((_, i) => i !== idx));
    setFieldError(null);
  };

  const handleSave = async () => {
    if (!person.id || saving) return;
    setSaving(true);
    setServerError(null);
    setFieldError(null);
    let latest: Person | null = null;
    try {
      if (skillsDirty) {
        latest = await apiClient.replaceSkills(
          person.id,
          skills.map(skillToPayload)
        );
      }
    } catch (e: any) {
      applyServerError(e, 'skills');
      setSaving(false);
      return;
    }
    try {
      if (domainsDirty) {
        latest = await apiClient.replaceDomains(
          person.id,
          domains.map(domainToPayload)
        );
      }
    } catch (e: any) {
      applyServerError(e, 'domains');
      setSaving(false);
      // Skills may have saved; push the latest person up so the parent
      // reflects the half-successful write.
      if (latest) onSaved(latest);
      return;
    }
    setSaving(false);
    if (latest) {
      onSaved(latest);
    } else {
      // Nothing dirty — no-op, just close.
      onClose();
    }
  };

  const applyServerError = (e: any, collection: 'skills' | 'domains') => {
    const data = e?.response?.data ?? {};
    const message = data.error || e?.message || `Failed to save ${collection}`;
    if (typeof data.field === 'string') {
      setFieldError({ field: data.field, message });
      // Scroll the offending row into view.
      const parsed = parseErrorField(data.field);
      if (parsed?.index != null && parsed.collection === collection) {
        const testid = `${collection}-editor-row-${parsed.index}`;
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="${testid}"]`);
          if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
            (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      }
    } else {
      setServerError(message);
    }
  };

  // Rendering -----------------------------------------------------------------

  const showCreateRow =
    !!q && !exactMatch && isAdmin && typeaheadMatches.every((m) => m.name.toLowerCase() !== q);

  return (
    <div
      ref={modalTrapRef}
      className="fixed inset-0 z-[70] flex flex-col bg-dark-bg"
      data-testid="skills-editor-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="skills-editor-title"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sticky top bar */}
      <div className="flex items-center justify-between gap-4 p-4 border-b border-dark-border bg-dark-card">
        <div className="min-w-0">
          <h2 id="skills-editor-title" className="text-lg font-bold text-dark-text">
            Edit skills &amp; domains — {person.Name || 'Unknown'}
          </h2>
          <p className="text-xs text-dark-text-secondary mt-0.5">
            {skills.length} {skills.length === 1 ? 'skill' : 'skills'} ·{' '}
            {domains.length} {domains.length === 1 ? 'domain' : 'domains'} · stored as{' '}
            <code className="text-amber-200">admin_edit</code> (CV re-imports won't
            overwrite).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => confirmClose(onClose)}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-dark-bg border border-dark-border text-dark-text-secondary hover:text-dark-text disabled:opacity-50"
            data-testid="skills-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="skills-editor-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {serverError && (
          <p
            className="text-sm text-red-400"
            role="alert"
            data-testid="skills-editor-error"
          >
            {serverError}
          </p>
        )}
        {fieldError && parsedError?.collection == null && (
          <p
            className="text-sm text-red-400"
            role="alert"
            data-testid="skills-editor-field-error-global"
          >
            {fieldError.message}
          </p>
        )}

        {/* --- Typeahead --- */}
        <section>
          <label
            htmlFor="skills-editor-typeahead"
            className="block text-xs font-bold uppercase tracking-wide text-dark-text-secondary mb-2"
          >
            Add skill
          </label>
          <div className="relative">
            <input
              id="skills-editor-typeahead"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                catalogLoading ? 'Loading catalog…' : 'Type to search the skills catalog…'
              }
              className="w-full px-3 py-2 rounded-md bg-dark-bg border border-dark-border text-dark-text focus:outline-none focus:border-indigo-500"
              data-testid="skills-editor-typeahead-input"
              autoComplete="off"
            />
            {q && (typeaheadMatches.length > 0 || showCreateRow) && (
              <ul
                className="absolute left-0 right-0 top-full mt-1 z-10 rounded-md border border-dark-border bg-dark-card shadow-lg max-h-72 overflow-y-auto"
                data-testid="skills-editor-typeahead-dropdown"
              >
                {typeaheadMatches.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => attachSkill(m)}
                      className="w-full text-left px-3 py-2 text-sm text-dark-text hover:bg-indigo-900/40 flex items-center justify-between"
                      data-testid={`skills-editor-typeahead-option-${m.id}`}
                    >
                      <span>{m.name}</span>
                      <span className="text-xs text-dark-text-secondary font-mono">
                        {m.id}
                      </span>
                    </button>
                  </li>
                ))}
                {showCreateRow && (
                  <li>
                    <button
                      type="button"
                      onClick={handleCreateNewSkill}
                      disabled={creating}
                      className="w-full text-left px-3 py-2 text-sm text-emerald-300 hover:bg-emerald-900/30 disabled:opacity-50"
                      data-testid="skills-editor-typeahead-create"
                    >
                      {creating ? 'Creating…' : `+ Create new skill "${query.trim()}" · admin only`}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        </section>

        {/* --- Skills list --- */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-dark-text">
              Skills ({skills.length})
            </h3>
            <p className="text-xs text-dark-text-secondary">role · proficiency · years</p>
          </div>
          {skills.length === 0 ? (
            <div
              className="text-center py-8 text-dark-text-secondary text-sm border border-dashed border-dark-border rounded-lg"
              data-testid="skills-editor-empty"
            >
              No skills yet. Use the typeahead above to add one.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="skills-editor-skills-list">
              {skills.map((s, idx) => {
                const rowError =
                  parsedError?.collection === 'skills' && parsedError.index === idx
                    ? fieldError?.message
                    : null;
                return (
                  <li
                    key={s.localId}
                    className={`flex items-center gap-3 p-2 rounded-md bg-dark-bg border ${
                      rowError ? 'border-red-500' : 'border-dark-border'
                    }`}
                    data-testid={`skills-editor-row-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-dark-text font-medium truncate">
                        {s.skill_name}
                      </div>
                      <div className="text-xs text-dark-text-secondary font-mono truncate">
                        {s.skill_id}
                      </div>
                    </div>
                    <select
                      value={s.skill_role}
                      onChange={(e) =>
                        patchSkill(idx, (prev) => ({ ...prev, skill_role: e.target.value }))
                      }
                      className="px-2 py-1 text-xs rounded bg-dark-card border border-dark-border text-dark-text"
                      data-testid={`skills-editor-row-${idx}-role`}
                      aria-label="skill role"
                    >
                      <option value="">role…</option>
                      {SKILL_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <select
                      value={s.proficiency}
                      onChange={(e) =>
                        patchSkill(idx, (prev) => ({ ...prev, proficiency: e.target.value }))
                      }
                      className="px-2 py-1 text-xs rounded bg-dark-card border border-dark-border text-dark-text"
                      data-testid={`skills-editor-row-${idx}-proficiency`}
                      aria-label="proficiency"
                    >
                      <option value="">proficiency…</option>
                      {SKILL_PROFICIENCIES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={0}
                      max={80}
                      value={s.years_experience}
                      onChange={(e) =>
                        patchSkill(idx, (prev) => ({
                          ...prev,
                          years_experience: e.target.value,
                        }))
                      }
                      placeholder="yrs"
                      className={YEARS_EXPERIENCE_INPUT_CLASS}
                      data-testid={`skills-editor-row-${idx}-years`}
                      aria-label="years of experience"
                    />
                    <button
                      type="button"
                      onClick={() => removeSkill(idx)}
                      className="px-2 py-1 text-sm text-red-400 hover:text-red-300"
                      data-testid={`skills-editor-row-${idx}-remove`}
                      aria-label="remove skill"
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {rowErrorText(parsedError, fieldError, 'skills')}
        </section>

        {/* --- Domains list --- */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-dark-text">
              Industries / domains ({domains.length})
            </h3>
            <p className="text-xs text-dark-text-secondary">
              Full-wipe on save · UNIQUE(person, domain) prevents shadow layering.
            </p>
          </div>
          {domains.length === 0 ? (
            <div
              className="text-center py-6 text-dark-text-secondary text-sm border border-dashed border-dark-border rounded-lg"
              data-testid="domains-editor-empty"
            >
              No domains yet.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="domains-editor-list">
              {domains.map((d, idx) => {
                const rowError =
                  parsedError?.collection === 'domains' && parsedError.index === idx
                    ? fieldError?.message
                    : null;
                return (
                  <li
                    key={d.localId}
                    className={`flex items-center gap-3 p-2 rounded-md bg-dark-bg border ${
                      rowError ? 'border-red-500' : 'border-dark-border'
                    }`}
                    data-testid={`domains-editor-row-${idx}`}
                  >
                    <input
                      type="text"
                      value={d.domain}
                      onChange={(e) =>
                        patchDomain(idx, (prev) => ({ ...prev, domain: e.target.value }))
                      }
                      placeholder="e.g. fintech, enterprise SaaS"
                      className="flex-1 px-2 py-1 text-sm rounded bg-dark-card border border-dark-border text-dark-text"
                      data-testid={`domains-editor-row-${idx}-text`}
                      aria-label="domain"
                    />
                    <input
                      type="number"
                      min={0}
                      max={80}
                      value={d.years_experience}
                      onChange={(e) =>
                        patchDomain(idx, (prev) => ({
                          ...prev,
                          years_experience: e.target.value,
                        }))
                      }
                      placeholder="yrs"
                      className={YEARS_EXPERIENCE_INPUT_CLASS}
                      data-testid={`domains-editor-row-${idx}-years`}
                      aria-label="years of experience"
                    />
                    <button
                      type="button"
                      onClick={() => removeDomain(idx)}
                      className="px-2 py-1 text-sm text-red-400 hover:text-red-300"
                      data-testid={`domains-editor-row-${idx}-remove`}
                      aria-label="remove domain"
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={addDomain}
            className="mt-2 px-3 py-1 text-sm rounded bg-dark-bg border border-dashed border-dark-border text-dark-text-secondary hover:text-dark-text"
            data-testid="domains-editor-add"
          >
            + Add domain
          </button>
          {rowErrorText(parsedError, fieldError, 'domains')}
        </section>
      </div>
    </div>
  );
};

function rowErrorText(
  parsed: ParsedError | null,
  fieldError: { field: string; message: string } | null,
  collection: 'skills' | 'domains'
) {
  if (!fieldError || !parsed) return null;
  if (parsed.collection !== collection) return null;
  if (parsed.index == null) return null;
  return (
    <p
      className="mt-2 text-xs text-red-400"
      role="alert"
      data-testid={`${collection}-editor-row-error`}
    >
      {fieldError.message}
    </p>
  );
}
